import { IconPlayerPlayFilled, IconEye } from '@tabler/icons-react';
import TeeBadge from '@/components/ui/tee-badge';
import { ContentMediaType } from '@/types/contentItem';
import { MEDIA_TYPE_ICON } from '@/utils/contentMediaType';

export interface TeeContentThumbnailProps {
  title: string;
  /** Omit when the underlying content item is unavailable (e.g. deleted out of band) — falls back to fallbackLabel only. */
  mediaType?: ContentMediaType;
  thumbnailUrl?: string | null;
  /** Sizing/aspect-ratio classes for the thumbnail box, e.g. "h-10 w-10" or "aspect-video w-full". */
  className?: string;
  /** Size of the fallback media-type glyph and the video-play / preview-eye overlay icons. */
  iconSize?: number;
  /**
   * Shows a hover "eye" overlay signalling the thumbnail is clickable to preview.
   * Skipped for VIDEO items, which already show a permanent play-icon overlay.
   * The caller's interactive trigger element (button / role="button") must carry
   * a `group` class for the hover overlay to activate.
   */
  previewable?: boolean;
  /** When set, the no-thumbnail fallback renders this label as a TeeBadge instead of a bare media-type icon glyph. */
  fallbackLabel?: string;
}

// Single shared thumbnail renderer for content items: image/fallback + a
// permanent video-play overlay + an optional hover preview-eye overlay.
// Previously reimplemented independently (and inconsistently — three of the
// four copies gave no visual cue at all for non-video previewable items) in
// ResourceFinderCard, ContentStudioPage, TeePlaylistReorderList, and
// TrainingProgramTab.
export function TeeContentThumbnail({
  title,
  mediaType,
  thumbnailUrl,
  className = 'h-10 w-10',
  iconSize = 20,
  previewable = false,
  fallbackLabel,
}: TeeContentThumbnailProps) {
  const MediaIcon = mediaType ? MEDIA_TYPE_ICON[mediaType] : null;
  const isVideo = mediaType === ContentMediaType.VIDEO;
  const overlayIconSize = Math.round(iconSize * 0.8);

  return (
    <div
      className={`relative flex shrink-0 items-center justify-center overflow-hidden rounded-[6px] bg-[var(--background-page-header)] ${className}`}
    >
      {thumbnailUrl ? (
        <img src={thumbnailUrl} alt={title} className="h-full w-full object-cover" />
      ) : fallbackLabel ? (
        <TeeBadge label={fallbackLabel} size="s" type="grey" />
      ) : MediaIcon ? (
        <MediaIcon size={iconSize} className="text-[var(--text-content-sub-title)]" />
      ) : null}

      {isVideo && thumbnailUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/25">
          <IconPlayerPlayFilled size={overlayIconSize} className="text-white" />
        </div>
      )}

      {previewable && !isVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-opacity duration-150 group-hover:bg-black/35 group-hover:opacity-100">
          <IconEye size={overlayIconSize} className="text-white" />
        </div>
      )}
    </div>
  );
}

export default TeeContentThumbnail;
